
import { useState, useMemo, useCallback } from 'react';

export interface SortConfig<T = any> {
  key: string;
  direction: 'asc' | 'desc';
}

interface SortableDataOptions {
  key?: string;
  direction?: 'asc' | 'desc';
}

export function useSortableData<T>(
  items: T[] = [], 
  initialConfig: SortableDataOptions = {}
) {
  // Initialize with the provided initial configuration or defaults
  const [sortConfig, setSortConfig] = useState<SortConfig<T>>({
    key: initialConfig.key || '',
    direction: initialConfig.direction || 'asc'
  });

  const handleSort = useCallback((key: string) => {
    setSortConfig(prevSortConfig => {
      if (prevSortConfig.key === key) {
        // Toggle direction if same key
        return {
          key,
          direction: prevSortConfig.direction === 'asc' ? 'desc' : 'asc'
        };
      }
      // New key, default to ascending
      return { key, direction: 'asc' };
    });
  }, []);

  const getByPath = useCallback((obj: any, path: string): unknown => {
    if (path === '' || obj == null) return undefined;
    const parts = path.split('.');
    let v: any = obj;
    for (const p of parts) {
      v = v?.[p];
    }
    return v;
  }, []);

  const sortData = useCallback((data: T[]) => {
    if (!sortConfig.key || data.length === 0) return data;
    const path = sortConfig.key;
    return [...data].sort((a: any, b: any) => {
      const aVal = getByPath(a, path);
      const bVal = getByPath(b, path);
      if (aVal === bVal) return 0;
      
      // Handle strings
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortConfig.direction === 'asc'
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      // Handle dates
      if (aVal instanceof Date && bVal instanceof Date) {
        return sortConfig.direction === 'asc'
          ? (aVal as any) - (bVal as any)
          : (bVal as any) - (aVal as any);
      }
      
      // Handle numbers and other types
      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;
      
      return sortConfig.direction === 'asc'
        ? aVal < bVal ? -1 : 1
        : aVal > bVal ? -1 : 1;
    });
  }, [sortConfig, getByPath]);

  const sortedItems = useMemo(() => {
    return sortData(items);
  }, [items, sortData]);

  return { 
    items: sortedItems, 
    sortConfig, 
    handleSort,
    sortData 
  };
}
